'use client'


import { Form, Spin } from 'antd';

import React, { useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux';
import Helper from '../../util/helper';
import { getCustomFields } from '../../redux/setting/reducer';
import formatMsg from '../../util/formatMsg';
import { notification } from '../../util/notification';
import { createAccount } from '../../redux/accounts/reducer';
import { getAllCentersList } from '../../redux/multicenter/reducer';
import { ModalWrapper } from '../wrapperComponents/page';
import FormWrapper from '../formWrapper/page';
import { permissions, timeutil } from 'sharedresources';


const AddEditAccountModal = (props) => {

    const {
        firebase,
        form,
        open,
        setOpen,
        phone,
        setPhone,
        editableRecords,
        setEditableRecords
    } = props;

    const { settingReducer, accountReducer, multicenterReducer } = Helper.getSelectorState(["settingReducer", "accountReducer", "multicenterReducer"]);

    useEffect(() => {
        dispatch(getCustomFields({ firebase, fieldType: "account" }));
    }, [])

    useEffect(() => {
        // Fetch centers when modal opens
        if (open) {
            dispatch(getAllCentersList({ firebase }));
        }
    }, [open])

    const canManageCenter = (centerId) => {
        return firebase.teacher?.isSuperAdmin || firebase.teacher?.permissionMap[centerId]?.includes(permissions.PERMISSIONS.ACCOUNT_ADD);
    };

    const getCenterLabel = (centerId, index?: number) => {
        const allCentersList = multicenterReducer?.allCentersList || [];
        const centerFromList = allCentersList.find((item) => item.id === centerId);
        if (centerFromList?.name) return centerFromList.name;

        const centerFromMap = multicenterReducer?.centerIdToCenterObjMap?.get(centerId);
        if (centerFromMap?.name) return centerFromMap.name;

        const editableCentersList = editableRecords?.centersList || editableRecords?.centers;
        const centerFromEditableList = editableCentersList?.find((center) => (center.id === centerId || center.centerId === centerId));
        if (centerFromEditableList?.name) return centerFromEditableList.name;
        if (centerFromEditableList?.centerName) return centerFromEditableList.centerName;
        if (centerFromEditableList?.center?.name) return centerFromEditableList.center.name;

        const centerNameMap = editableRecords?.centerIdNameMap || editableRecords?.centerIdToNameMap;
        if (centerNameMap?.[centerId]) return centerNameMap[centerId];

        const centerNamesArr = editableRecords?.centerNames || editableRecords?.centersName;
        if (Array.isArray(centerNamesArr) && centerNamesArr[index] && typeof centerNamesArr[index] === "string") {
            return centerNamesArr[index];
        }

        return centerId;
    };

    const hiddenCenterIds = useMemo(() => {
        return (editableRecords?.centerIds ?? []).filter((centerId) => !canManageCenter(centerId));
    }, [editableRecords?.centerIds, firebase]);

    const permittedCenterOptions = (multicenterReducer?.allCentersList ?? [])
        .filter((center) => canManageCenter(center.id))
        .map((center) => ({
            label: center.name,
            value: center.id
        }));

    useEffect(() => {
        if (editableRecords) {
            const editableCenterOptions = (editableRecords?.centerIds ?? [])
                .map((centerId, index) => ({
                    label: getCenterLabel(centerId, index),
                    value: centerId
                }));
            let obj = { ...editableRecords };
            obj.centerIds = editableCenterOptions;
            form.setFieldsValue({
                ...obj
            });
        }
    }, [
        editableRecords,
        multicenterReducer?.allCentersList,
        multicenterReducer?.centerIdToCenterObjMap,
        editableRecords?.centersList,
        editableRecords?.centers,
        editableRecords?.centerNames,
        editableRecords?.centersName,
    ])


    useEffect(() => {

        const operationType = accountReducer.operationType;

        if (
            operationType &&
            (operationType === "ACCOUNT_CREATED" || operationType === "ACCOUNT_UPDATED" || operationType === "ACCOUNT_DELETED")
        ) {
            handleCancel();
        }

    }, [accountReducer.operationType]);

    const dispatch = useDispatch();

    const AccountFields = [
        [{
            label: formatMsg("addProfilePhoto"),
            type: "media",
            name: "profileImageUrl",
            placeHolder: formatMsg("addProfilePhoto"),
            warningMessage: "Please select profile photo!",
            inputType: "picture",
            values: form.getFieldValue("profileImageUrl")
        }],
        [{
            label: formatMsg("name"),
            type: "input",
            name: "name",
            placeHolder: formatMsg("name"),
            required: true,
            warningMessage: "Please input name!",
            inputType: "string",
        }],
        [{
            label: formatMsg("phoneNumber"),
            type: "phoneInput",
            name: "phoneNumber",
            placeHolder: formatMsg("phoneNumber"),
            warningMessage: "Please input phone number!",
            required: settingReducer && settingReducer.defaultFieldsNameToObjMap ? settingReducer.defaultFieldsNameToObjMap.get('Phone Number')?.mandatory : true,
            inputType: "phoneInput",
            onPhoneChange: (value, data) => {
                setPhone({
                    phoneNumber: value ? "+" + value : null,
                    countryCode: "+" + data.dialCode,
                })
            },
            phoneValue: phone.phoneNumber ?
                phone.phoneNumber.toString().slice(1) :
                undefined
        }],
        [{
            label: formatMsg("placeholder.email"),
            type: "input",
            name: "email",
            placeHolder: formatMsg("placeholder.email"),
            required: settingReducer && settingReducer.defaultFieldsNameToObjMap ? settingReducer.defaultFieldsNameToObjMap.get('Email')?.mandatory : true,
            warningMessage: "Please input email!",
            inputType: "email",
        }],
        [{
            label: formatMsg("centers"),
            type: "select",
            name: "centerIds",
            placeHolder: formatMsg("selectCenters"),
            required: true,
            warningMessage: "Please select centers!",
            inputType: "select",
            mode: "multiple",
            options: permittedCenterOptions,
            optionKey: "value",
            optionLabel: "label",
            labelInValue: true
        }],
    ]

    function handleOk() {

        form.validateFields()
            .then((values) => {
                const { defaultFieldsNameToObjMap } = settingReducer;

                if (defaultFieldsNameToObjMap) {
                    if (defaultFieldsNameToObjMap.get('Phone Number')?.mandatory && !phone.phoneNumber) {
                        notification("error", formatMsg("error.phone"));
                        return;
                    }
                    else if (defaultFieldsNameToObjMap.get('Email')?.mandatory && !form.getFieldValue("email")) {
                        notification("error", formatMsg("error.email"));
                        return;
                    }
                }
                else {
                    notification("error", formatMsg("error.oopsSomthingWentWrong"));
                    return;
                }

                values.status = (editableRecords && editableRecords.status) || "ACTIVE";
                values.id = editableRecords && editableRecords.id;
                if (!values.profileImageUrl && editableRecords) {
                    values.profileImageUrl = editableRecords.profileImageUrl
                }
                values.childConfig = settingReducer.customFields ?
                    settingReducer.customFields.map((additionalFieldObj) => {
                        const tempAdditionalFieldObj = { ...additionalFieldObj };
                        tempAdditionalFieldObj.value = (values[additionalFieldObj.name]) ?
                            (additionalFieldObj.dataType === 'date' || additionalFieldObj.dataType === 'time') ?
                                timeutil.getEpoch(timeutil.getInstance(values[additionalFieldObj.name]))
                                :
                                values[additionalFieldObj.name]
                            :
                            null;
                        delete values[additionalFieldObj.name];
                        return tempAdditionalFieldObj;
                    })
                    :
                    null;

                values.phoneNumber = phone.phoneNumber;
                values.migratedFromIllumine = editableRecords?.migratedFromIllumine || null;
                const selectedCenterIds = values.centerIds?.map((center) => center.value) ?? [];
                values.centerIds = Array.from(new Set([...(hiddenCenterIds ?? []), ...selectedCenterIds]));
                dispatch(createAccount({ reqBody: values, firebase }));
            })
            .catch((error) => {
                notification("error", formatMsg("pleaseFillAllDetails"))
                console.error('Validation failed:', error);
            });
    }


    function handleCancel() {
        form.resetFields();
        setOpen(undefined);
        if (setEditableRecords) setEditableRecords(undefined);
        setPhone({ phoneNumber: undefined, countryCode: undefined });
    }

    return (
        <ModalWrapper
            open={open}
            title={formatMsg("accountDetails")}
            onOk={handleOk}
            onCancel={handleCancel}
            okText={formatMsg("submit")}
            maskClosable={false}
            confirmLoading={accountReducer.isLoading || multicenterReducer.isLoading}
        >
            <Spin spinning={multicenterReducer.isLoading}>
                <Form
                    form={form}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                    initialValues={{ remember: true }}
                >
                    <FormWrapper
                        form={form}
                        formFields={[...AccountFields, ...Helper.getAdditionalFormFields(settingReducer?.customFields)]}
                        editableRecords={editableRecords}
                        setEditableRecords={setEditableRecords}
                    />
                </Form>
            </Spin>
        </ModalWrapper>
    )
}

export default AddEditAccountModal